import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { UserPlus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { apiPutPhysiciansUuid } from "../../../api";
import {
  PatientMonitoringType,
  PatientStatus,
} from "../../../api/generated/types.gen";
import { AppModel } from "../../../app/app-model";
import { PageHeaderActions } from "../../../app/layout/layout-context";
import { useCountries } from "../../../catalogs/countries";
import { PatientTableSearch } from "../../../components/patient-search-palette";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../../../components/ui-kit/base/tabs";
import { Button } from "../../../components/ui-kit/button";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { Spinner } from "../../../components/ui-kit/spinner";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { managingRoles } from "../../../constants/constants";
import {
  monitoringTypes,
  patientStatuses,
} from "../../../constants/enum-to-text";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
} from "../../../utils/filter-presets";
import { physicianName } from "../../../utils/physician";
import {
  PatientStatusCell,
  PatientStatusIndex,
} from "../common/patient-status-breakdown";
import { PhysiciansModal } from "../common/physicians-modal";
import { UsersModal } from "../common/users-modal";

type TabId = "patients" | "physicians";

export const MonitoringManagementRoute = observer(
  function MonitoringManagementRoute({ app }: { app: AppModel }) {
    const { t } = useLingui();
    const admin = app.requireAdminShit();
    const countries = useCountries();

    const [activeTab, setActiveTab] = useState<TabId>("patients");

    const physiciansModel = useMemo(
      () => admin.createOrphanPhysicians(),
      [admin],
    );
    // The orphan-patients fetch returns all visible patients, so it also
    // populates the status index — no separate index fetch needed here.
    const statusIndex = useMemo(() => new PatientStatusIndex(), []);
    const patientsModel = useMemo(
      () => admin.createOrphanPatients(statusIndex),
      [admin, statusIndex],
    );

    // The patients table opens with this filter applied (active or onboarded,
    // self-measurement) — onboarded patients are new, so they are the ones
    // most likely to still be missing a physician. The tab badge below shows
    // the unfiltered total instead: DataTable owns the filter state, so the
    // model can't report the filtered count.
    const defaultPatientFilters = [
      {
        id: "patient_status",
        value: [PatientStatus.active, PatientStatus.onboarded],
      },
      {
        id: "monitoring_type",
        value: [PatientMonitoringType.self_measurement],
      },
    ];

    const patientCount = patientsModel.initialized
      ? patientsModel.data.length
      : "–";
    const physicianCount = physiciansModel.initialized
      ? physiciansModel.data.length
      : "–";

    return (
      <>
        {activeTab === "patients" && (
          <PageHeaderActions>
            <PatientTableSearch
              rows={patientsModel.data}
              patient={(item) => ({
                id: item.patient_id,
                name: item.full_name,
                subtitle: [
                  item.patient_status != null &&
                    resolveEnumLabel(patientStatuses[item.patient_status]),
                  item.monitoring_type != null &&
                    resolveEnumLabel(monitoringTypes[item.monitoring_type]),
                  item.country,
                ],
              })}
            />
          </PageHeaderActions>
        )}
        <Tabs
          className="mb-5"
          value={activeTab}
          onValueChange={(id: string) => setActiveTab(id as TabId)}
        >
          <TabsList>
            <TabsTrigger value="patients">
              <Trans>Patients without physicians ({patientCount})</Trans>
            </TabsTrigger>
            <TabsTrigger value="physicians">
              <Trans>
                Physicians without monitoring users ({physicianCount})
              </Trans>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {activeTab === "patients" ? (
          patientsModel.initialized ? (
            <DataTable
              model={patientsModel}
              itemKeyField="patient_id"
              filterNoun={(count) =>
                t`${plural(count, { one: "patient", other: "patients" })}`
              }
              initialColumnFilters={defaultPatientFilters}
              columns={[
                {
                  header: <Trans>Patient ID</Trans>,
                  attr: "patient_id",
                  filter: {
                    kind: "text",
                    group: t`Identity`,
                    placeholder: t`Search…`,
                  },
                  width: 100,
                  cell: ({ item }) => (
                    <strong>
                      <Link to={`/patient/${item.patient_id}`}>
                        {item.patient_id}
                      </Link>
                    </strong>
                  ),
                },
                {
                  header: <Trans>Status</Trans>,
                  attr: "patient_status",
                  type: "enum",
                  values: patientStatuses,
                  filter: {
                    kind: "multi",
                    group: t`Status`,
                    options: new Map(Object.entries(patientStatuses)),
                  },
                  width: 100,
                },
                {
                  header: <Trans>Name</Trans>,
                  attr: "full_name",
                  type: "string",
                  filter: {
                    kind: "text",
                    group: t`Identity`,
                    placeholder: t`Search…`,
                  },
                  width: 100,
                },
                {
                  header: <Trans>Country</Trans>,
                  attr: "country",
                  type: "string",
                  filter: {
                    kind: "multi",
                    group: t`Location`,
                    options: getCountryOptions,
                  },
                  width: 100,
                },
                {
                  header: <Trans>Added</Trans>,
                  attr: "created_at",
                  type: "date",
                  sort: true,
                  width: 100,
                },
                {
                  header: <Trans>Monitoring type</Trans>,
                  attr: "monitoring_type",
                  type: "enum",
                  values: monitoringTypes,
                  filter: {
                    kind: "multi",
                    group: t`Status`,
                    options: new Map(Object.entries(monitoringTypes)),
                  },
                },
                {
                  header: <Trans>Assign physician</Trans>,
                  width: 60,
                  sticky: "right",
                  cell: ({ item }) => {
                    const name = item.full_name ?? `#${item.patient_id}`;
                    return (
                      <AssignButton
                        tooltip={t`Assign physician`}
                        modal={({ onClose }) => (
                          <PhysiciansModal
                            model={admin.createPhysicians()}
                            mode="multi"
                            geoLock={{
                              country: item.country,
                              region: item.region,
                            }}
                            showMonitoringUsers
                            title={t`Assign physician(s) to patient: ${name}`}
                            onClose={onClose}
                            onConfirm={async ({ selected }) => {
                              await Promise.all(
                                selected.map((uuid: string) =>
                                  apiPutPhysiciansUuid({
                                    path: { uuid },
                                    body: {
                                      append_patients: [item.patient_id],
                                    },
                                  }),
                                ),
                              );
                              await patientsModel.refresh();
                            }}
                          />
                        )}
                      />
                    );
                  },
                },
              ]}
            />
          ) : (
            <Spinner />
          )
        ) : physiciansModel.initialized ? (
          <DataTable
            model={physiciansModel}
            itemKeyField="uuid"
            filterNoun={(count) =>
              t`${plural(count, { one: "physician", other: "physicians" })}`
            }
            columns={[
              {
                header: <Trans>Name</Trans>,
                attr: "full_name",
                type: "string",
                sort: true,
                width: 200,
                filter: {
                  kind: "text",
                  group: t`Identity`,
                  placeholder: t`Search…`,
                },
              },
              {
                header: <Trans>Country</Trans>,
                attr: "country",
                type: "string",
                filter: {
                  kind: "multi",
                  group: t`Location`,
                  options: getCountryOptions,
                },
              },
              {
                header: <Trans>Region</Trans>,
                attr: "region",
                filter: {
                  kind: "multi",
                  group: t`Location`,
                  options: (table) => getRegionOptions(table, countries),
                  disabled: regionDisabled,
                  disabledMessage: t`Select a country first`,
                  predicate: regionPredicate,
                },
                cell: ({ item }) => {
                  const name =
                    item.country && item.region != null
                      ? countries.getRegionsMap(item.country).get(item.region)
                      : undefined;
                  return name ?? item.region ?? "—";
                },
              },
              {
                header: <Trans>Email</Trans>,
                attr: "email",
                type: "string",
              },
              {
                header: <Trans>Clinics</Trans>,
                attr: "numClinics",
                type: "number",
                width: 30,
              },
              {
                header: <Trans>Patients</Trans>,
                attr: "numPatients",
                type: "number",
                sort: true,
                width: 220,
                align: "left",
                cell: ({ item }) => (
                  <PatientStatusCell
                    patients={item.patients}
                    index={statusIndex}
                  />
                ),
              },
              {
                header: <Trans>Assign monitoring user</Trans>,
                sticky: "right",
                width: 60,
                cell: ({ item }) => (
                  <AssignButton
                    tooltip={t`Assign monitoring user`}
                    modal={({ onClose }) => (
                      <UsersModal
                        model={admin.createMonitoringUsers(
                          item.bypass_geography_checks?.[0]
                            ? undefined
                            : item.country,
                          item.bypass_geography_checks?.[1]
                            ? undefined
                            : item.region,
                        )}
                        mode="multi"
                        filter={{ role: managingRoles }}
                        title={t`Assign monitoring user(s) to physician: ${physicianName(item)}`}
                        onClose={onClose}
                        onConfirm={async ({ selected }) => {
                          await apiPutPhysiciansUuid({
                            path: { uuid: item.uuid },
                            body: { assigned_users: selected },
                          });
                          await physiciansModel.refresh();
                        }}
                      />
                    )}
                  />
                ),
              },
            ]}
          />
        ) : (
          <Spinner />
        )}
      </>
    );
  },
);

function AssignButton({
  tooltip,
  modal,
}: {
  tooltip: string;
  modal: (props: { onClose: () => void }) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  const modalRef = useRef(modal);
  modalRef.current = modal;

  // Build the modal element once per open cycle so model construction inside
  // the builder (e.g. admin.createPhysicians()) doesn't repeat on parent re-renders.
  const modalElement = useMemo(
    () => (open ? modalRef.current({ onClose: close }) : null),
    [open, close],
  );

  return (
    <>
      <Button
        variant="subtle"
        title={tooltip}
        aria-label={tooltip}
        onClick={() => setOpen(true)}
      >
        <UserPlus className="size-4" />
      </Button>
      {modalElement}
    </>
  );
}
